import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { forgetAnswers } from '@/features/survey/draft'
import { SurveyRunner } from '@/features/survey/SurveyRunner'
import type { Extra, Guard, Suggest } from '@/features/survey/SurveyRunner'
import { useEvaluateRoadmap, useRestartSurvey, useSurvey } from '@/shared/api/queries'
import type { Roadmap, SurveyQuestion } from '@/shared/api/queries'
import { useLocalization } from '@/shared/i18n'
import { countryNames } from '@/shared/model'
import { ActionSheet, Alert, Button, Chip, Progress, Skeleton, Stack, Text } from '@/shared/ui'

import { Building } from './steps/Building'
import { Payoff } from './steps/Payoff'
import { Welcome } from './steps/Intro'
import { popularCities, popularCitizenships } from './suggestions'

export const ONBOARDING_SURVEY = 'onboarding'

const COUNTRY = 'onboarding-country'
const CITIZENSHIP = 'onboarding-citizenship'
const CITY = 'onboarding-city'

const DEFAULT_STEPS = 6

type Phase = 'welcome' | 'survey' | 'building' | 'payoff'

interface Moving {
  to: string
  proceed: (instead?: string) => void
}

export function OnboardingWizard({
  stored,
  onFinished,
}: {
  stored?: string | undefined
  onFinished: () => void
}) {
  const existing = Boolean(stored)
  const { t, language } = useLocalization()
  // Somebody who already has a profile came here to change an answer, not to be
  // told what the product is for.
  const [phase, setPhase] = useState<Phase>(existing ? 'survey' : 'welcome')
  const [roadmap, setRoadmap] = useState<Roadmap>()
  const [country, setCountry] = useState<string>()
  const [moving, setMoving] = useState<Moving>()

  const survey = useSurvey(ONBOARDING_SURVEY)
  const evaluate = useEvaluateRoadmap()
  const open = useOpenRun(survey.data?.status)

  const names = useMemo(() => countryNames(language), [language])
  const nameOf = useCallback(
    (code: string) => names.find((named) => named.code === code)?.name ?? code,
    [names],
  )

  const around = country ?? stored ?? ''
  const progress = survey.data?.progress
  const steps = progress ? progress.answered + progress.remaining : DEFAULT_STEPS

  const build = useCallback(() => {
    evaluate.mutate(undefined, { onSuccess: setRoadmap })
  }, [evaluate])

  const completed = useCallback(() => {
    forgetAnswers(ONBOARDING_SURVEY)
    setRoadmap(undefined)
    setPhase('building')
    build()
  }, [build])

  const reveal = useCallback(() => setPhase('payoff'), [])

  const guard: Guard = (question, value, proceed) => {
    if (question.tag !== COUNTRY || typeof value !== 'string') {
      return false
    }

    setCountry(value)

    if (!stored || stored === value) {
      return false
    }

    setMoving({ to: value, proceed })

    return true
  }

  const suggest: Suggest = (question) =>
    question.tag === CITIZENSHIP ? popularCitizenships(around) : []

  const extra: Extra = (question, value, onChange) => (
    <Suggestions question={question} value={value} around={around} onChange={onChange} />
  )

  function rail(value: number) {
    return <Progress steps value={value} max={steps} label={t('onboarding.title')} />
  }

  if (phase === 'welcome') {
    return <Welcome rail={rail(0)} onNext={() => setPhase('survey')} />
  }

  if (phase === 'payoff' && roadmap) {
    return <Payoff rail={rail(steps)} roadmap={roadmap} onFinish={onFinished} />
  }

  if (phase === 'building') {
    return (
      <Building
        rail={rail(steps)}
        country={nameOf(around)}
        ready={Boolean(roadmap)}
        failure={evaluate.error}
        onRetry={build}
        onDone={reveal}
      />
    )
  }

  if (!open) {
    return <Skeleton variant="row" rows={4} title />
  }

  return (
    <>
      <SurveyRunner
        tag={ONBOARDING_SURVEY}
        onLeave={onFinished}
        onBeforeStart={existing ? undefined : () => setPhase('welcome')}
        onCompleted={completed}
        extra={extra}
        suggest={suggest}
        guard={guard}
      />

      {moving && stored ? (
        <ActionSheet
          title={t('onboarding.country.change.title', { country: nameOf(moving.to) })}
          onClose={() => setMoving(undefined)}
        >
          <Text tone="muted" measure>
            {t('onboarding.country.change.body', { from: nameOf(stored), to: nameOf(moving.to) })}
          </Text>
          <Button
            full
            onClick={() => {
              setMoving(undefined)
              moving.proceed()
            }}
          >
            {t('onboarding.country.change.accept', { country: nameOf(moving.to) })}
          </Button>
          <Button
            full
            variant="quiet"
            onClick={() => {
              setMoving(undefined)
              setCountry(stored)
              moving.proceed(stored)
            }}
          >
            {t('onboarding.country.change.stay', { country: nameOf(stored) })}
          </Button>
        </ActionSheet>
      ) : null}
    </>
  )
}

function useOpenRun(status: string | undefined): boolean {
  const restart = useRestartSurvey()
  const [open, setOpen] = useState(false)
  const asked = useRef(false)

  if (!open && status !== undefined && status !== 'completed') {
    setOpen(true)
  }

  useEffect(() => {
    if (open || asked.current || status !== 'completed') {
      return
    }

    asked.current = true
    forgetAnswers(ONBOARDING_SURVEY)
    restart.mutate(ONBOARDING_SURVEY)
  }, [open, status, restart])

  return open
}

function Suggestions({
  question,
  value,
  around,
  onChange,
}: {
  question: SurveyQuestion
  value: unknown
  around: string
  onChange: (value: string | string[] | undefined) => void
}) {
  const { t } = useLocalization()

  if (question.tag === COUNTRY && (question.options ?? []).length === 0) {
    return (
      <Alert status="warning" title={t('onboarding.country.none.title')}>
        {t('onboarding.country.none.body')}
      </Alert>
    )
  }

  if (question.tag === CITY) {
    const cities = popularCities(around)

    if (cities.length === 0) {
      return null
    }

    return (
      <Stack direction="row" gap="sm" wrap className="ask__options">
        {cities.map((city) => (
          <Chip
            key={city.value}
            selected={value === city.value}
            onClick={() => onChange(value === city.value ? undefined : city.value)}
          >
            {t(city.key)}
          </Chip>
        ))}
      </Stack>
    )
  }

  return null
}
